import React, { useEffect, useState, useRef, useCallback } from "react";
import Layout from "../Layout/Layout";
import ShareDocumentModal from "../Components/ShareDocumentModal";
import axios from "axios";
import { API_BASE } from "../Utils/constant";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import { AUTH_TOKEN } from "../constants/localStorageKeys";
import { retrieve } from "../helpers/StorageHelper";
import { useQueryClient } from "@tanstack/react-query";
import {
  invalidateLevelQueries,
  invalidateHabitQueries,
} from "../Utils/queryInvalidation";
import {
  formatDate as formatDateForAPI,
  formatQuarterlyWeek,
} from "../helpers/DateHelper";

export default function HabitTracker() {
  const [startOfWeek, setStartOfWeek] = useState(null); // Initialize as null
  const [circles, setCircles] = useState([]);
  const [weekCount, setWeekCount] = useState("0/0");
  const [loading, setLoading] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileToShare, setFileToShare] = useState(null);
  const titleToShare = "Share you Habits";
  const contentToShare = "";

  const contentRef = useRef(null);

  const [isToggling, setIsToggling] = useState(false);

  const queryClient = useQueryClient();

  // Check if date is in future
  const isFutureDate = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return compareDate > today;
  }, []);

  const isPastDate = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return compareDate < today;
  }, []);

  // Update week count
  const updateWeekCount = useCallback((circlesData) => {
    const activeCount = circlesData.filter((circle) => circle.active).length;
    setWeekCount(`${activeCount}/${circlesData.length}`);
  }, []);

  // Process API data to create circles
  const processApiData = useCallback((data, weekStart) => {
    if (!data || !weekStart) return [];

    const initialCircles = [];
    let circleId = 0;

    Object.entries(data).forEach(([category, habits]) => {
      Object.entries(habits).forEach(([habitKey, habitData]) => {
        const habitName = habitData.type_name;
        const habitLogo = habitData.logo || null;

        for (let day = 0; day < 7; day++) {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + day);
          const dateStr = formatDateForAPI(date);

          const trackedDay = habitData.tracked_data?.find(
            (d) => d.habit_date === dateStr,
          );
          const isActive = trackedDay?.status === 1;

          initialCircles.push({
            id: circleId,
            category,
            habit: habitName,
            habitId: habitData.id,
            habitLogo,
            day,
            active: isActive,
          });
          circleId++;
        }
      });
    });

    return initialCircles;
  }, []);

  // Fetch habits data from API
  const fetchHabitsData = useCallback(
    async (fromDate, toDate, silent = false) => {
      if (!silent) setLoading(true);

      try {
        const token = retrieve(AUTH_TOKEN);
        const response = await axios.get(`${API_BASE}/user/habits`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            from_date: formatDateForAPI(fromDate),
            to_date: formatDateForAPI(toDate),
          },
        });

        setApiData(response.data.data);

        const processedCircles = processApiData(response.data.data, fromDate);
        setCircles(processedCircles);
        updateWeekCount(processedCircles);
      } catch (error) {
        console.error("Error fetching habits data:", error);
        toast.error("Failed to load habits data");
        setCircles([]);
        setWeekCount("0/0");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [processApiData, updateWeekCount],
  );

  // Initialize startOfWeek on component mount
  useEffect(() => {
    const currentDate = new Date();
    const currentDay = currentDate.getDay() - 1;
    const sunday = new Date(currentDate);
    sunday.setDate(currentDate.getDate() - currentDay);
    sunday.setHours(0, 0, 0, 0);
    setStartOfWeek(sunday);
  }, []);

  // Fetch data when startOfWeek changes
  useEffect(() => {
    if (!startOfWeek) return;

    const sunday = new Date(startOfWeek);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);

    const source = axios.CancelToken.source();

    fetchHabitsData(sunday, saturday);

    return () => {
      source.cancel("Operation canceled due to new request");
    };
  }, [startOfWeek, fetchHabitsData]);

  const handleGenerateAndOpenModal = async () => {
    if (!contentRef.current) return;

    const clonedContent = contentRef.current.cloneNode(true);

    const elementToRemove = clonedContent.querySelectorAll(
      ".btn.d-flex.justify-content-center.align-items-center.rounded-circle",
    );
    elementToRemove.forEach((el) => el.remove());

    const buttonToRemove = clonedContent.querySelector(".btn-3.btn-1-primary");
    if (buttonToRemove) {
      buttonToRemove.remove();
    }

    const style = document.createElement("style");
    style.innerHTML = `
      h5.text-white, h2, .text-body-b3{
        color: #000 !important;
      }

      .col-md-6{
        width: 100% !important;
      }

      .page-break {
        page-break-before: always; /* Forces a new page */
      }

      .no-break {
        page-break-inside: avoid; /* Prevents cutting inside this element */
      }

      .habit-sub-card{
        page-break-inside: avoid;
      }
    `;
    clonedContent.prepend(style);

    const options = {
      margin: 0.5,
      filename: "habit-tracker-report.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    try {
      const pdfBlob = await html2pdf()
        .from(clonedContent)
        .set(options)
        .outputPdf("blob");

      // Create a File object from the Blob
      const pdfFile = new File([pdfBlob], "habit-tracker-report.pdf", {
        type: "application/pdf",
      });
      setFileToShare(pdfFile);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFileToShare(null); // Clear the file after closing the modal
  };

  // Toggle circle status
  const toggleCircle = async (circleId) => {
    if (isToggling) return; // Prevent multiple toggles while loading

    const token = retrieve(AUTH_TOKEN);
    const circle = circles.find((c) => c.id === circleId);
    if (!circle || !token) return;

    // todo: uncomment this when journal is fully implemented

    // Journal completion is handled in the Journal flow.
    // if (circle.habit === "Journal") {
    //   toast.error("Complete AM journal to complete the journal habit.");
    //   return;
    // }

    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + circle.day);

    if (isPastDate(date)) {
      toast.error("Past records are locked to preserve streak integrity.");
      return;
    }

    if (isFutureDate(date)) {
      toast.error("Future dates are not allowed");
      return;
    }

    // if (isPastDate(date)) {
    //   toast.error("Past dates are not allowed");
    //   return;
    // }

    const dateStr = formatDateForAPI(date);
    const newStatus = !circle.active ? 1 : 0;

    setIsToggling(true);

    // Optimistic update
    const previousCircles = [...circles];
    const updatedCircles = circles.map((c) =>
      c.id === circleId ? { ...c, active: !c.active } : c,
    );
    setCircles(updatedCircles);
    updateWeekCount(updatedCircles);

    try {
      await axios.post(
        `${API_BASE}/user/habit/update-status`,
        {},
        {
          params: {
            habits_id: circle.habitId,
            habit_date: dateStr,
            status: Number(newStatus),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // Refetch data to ensure consistency
      const sunday = new Date(startOfWeek);
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);
      fetchHabitsData(sunday, saturday, true);

      // Use centralized query invalidation for level-up detection
      invalidateLevelQueries(queryClient);
      invalidateHabitQueries(queryClient);
    } catch (error) {
      console.error("Error updating habit:", error);
      setCircles(previousCircles);
      updateWeekCount(previousCircles);
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors;
        const firstError =
          errors?.habit_date?.[0] || "Failed to update habit status";
        toast.error(firstError);
      } else {
        toast.error("Failed to update habit status");
      }
    } finally {
      setIsToggling(false);
    }
  };

  // go to week
  const goToPreviousWeek = () => {
    setStartOfWeek((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setStartOfWeek((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
  };

  const getWeekDisplayString = () => {
    const endDate = new Date(startOfWeek);
    endDate.setDate(startOfWeek.getDate() + 6);

    const start = formatDate(startOfWeek);
    const end = formatDate(endDate);

    return `${start} - ${end}`;
  };

  const order = ["body", "balance", "mind", "wealth"];

  const renderHabitCircles = (category, habitName) => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
      <div className="week-circles">
        {days.map((day, index) => {
          const circle = circles.find(
            (c) =>
              c.category === category &&
              c.habit === habitName &&
              c.day === index,
          );

          // Calculate if this day is in the future
          const date = new Date(startOfWeek);
          date.setDate(startOfWeek.getDate() + index);
          const isFuture = isFutureDate(date);
          const isPast = isPastDate(date);

          return (
            <div className="day-column" key={index}>
              <div className="day-label">{day}</div>
              <div
                className={`circle ${circle && circle.active ? "active" : ""} ${
                  isToggling || isFuture || isPast
                    ? // || circle?.habit === "Journal"
                      "circle-disabled"
                    : ""
                }`}
                title={
                  isPast
                    ? "Past records are locked to preserve streak integrity."
                    : undefined
                  /*
                    : circle?.habit === "Journal"
                      ? "Journal updates are handled after completing AM Journal."
                      : undefined
                    */
                }
                onClick={() => {
                  if (isToggling || !circle) return;

                  if (isPast) {
                    toast.error(
                      "Past records are locked to preserve streak integrity.",
                    );
                    return;
                  }

                  if (isFuture) {
                    toast.error("Future dates are not allowed");
                    return;
                  }

                  /* TODO: uncomment this when journal is fully implemented
                  if (circle.habit === "Journal") {
                    toast.error(
                      "Complete AM journal to complete the journal habit.",
                    );
                    return;
                  }
                  */
                  toggleCircle(circle.id);
                }}
              ></div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderHabitCategory = (category, habits) => {
    const categoryHabits = [
      ...new Set(
        circles.filter((c) => c.category === category).map((c) => c.habit),
      ),
    ];

    return (
      <div>
        <h2 className="section-title">
          {category.charAt(0).toUpperCase() + category.slice(1)}
        </h2>
        <div className="habit-sub-card">
          {categoryHabits.map((habit, index) => (
            <div className="habit-row" key={index}>
              <div className="habit-name">
                <div className="habit-tracker-icon">
                  <img
                    className="habits-trackers-icons-sub"
                    src={
                      circles.find(
                        (c) => c.category === category && c.habit === habit,
                      )?.habitLogo || "/icons/cloud-lightning.svg"
                    }
                    alt={`${habit} icon`}
                  />
                </div>
                {habit.charAt(0).toUpperCase() + habit.slice(1)}
              </div>
              {renderHabitCircles(category, habit)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="page-content">
        <div className="setting-content">
          <h1 className="pages-header text-heading-h2">Habits</h1>
          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          ) : (
            <>
              <div className="habit-card">
                <div className="main-container" ref={contentRef}>
                  <div className="text-center mb-3">
                    <span
                      className="text-body-b1"
                      style={{ color: "#c8ced3", letterSpacing: "0.5px" }}
                    >
                      {startOfWeek && formatQuarterlyWeek(startOfWeek)}
                    </span>
                  </div>
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center align-items-start mb-3 habit-head-cd">
                    <div className="week-header mb-3 mb-md-0">
                      <h5 className="mb-1 text-white">This Week</h5>
                      <div className="week-count">{weekCount}</div>
                    </div>

                    <div className="date-nav d-flex align-items-center justify-content-center gap-4 mb-3 mb-md-0">
                      <button
                        className="btn d-flex justify-content-center align-items-center rounded-circle"
                        style={{
                          backgroundColor: "#1c2934",
                          width: "40px",
                          height: "40px",
                        }}
                        onClick={goToPreviousWeek}
                      >
                        <img
                          src="/icons/pagination-left-icon.svg"
                          alt="Previous"
                        />
                      </button>
                      <span
                        style={{ color: "#c8ced3" }}
                        className="text-body-b3 week-date-score"
                      >
                        {getWeekDisplayString()}
                      </span>
                      <button
                        className="btn d-flex justify-content-center align-items-center rounded-circle"
                        style={{
                          backgroundColor: "#1c2934",
                          width: "40px",
                          height: "40px",
                        }}
                        onClick={goToNextWeek}
                      >
                        <img
                          src="/icons/pagination-right-icon.svg"
                          alt="Next"
                        />
                      </button>
                    </div>

                    <div className="d-flex justify-content-center justify-content-md-end">
                      <button
                        className="btn-3 btn-1-primary"
                        onClick={handleGenerateAndOpenModal}
                      >
                        Share
                      </button>
                    </div>
                  </div>

                  {apiData && Object.keys(apiData).length > 0 ? (
                    <div className="row">
                      {order.map(
                        (category) =>
                          apiData[category] && (
                            <div className="col-md-6 mt-1" key={category}>
                              {renderHabitCategory(category, apiData[category])}
                            </div>
                          ),
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <p style={{ color: "#c8ced3" }}>
                        No habits found for this week.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* modal for share */}
      {isModalOpen && (
        <>
          <ShareDocumentModal
            show={isModalOpen}
            title={titleToShare}
            file={fileToShare}
            content={contentToShare}
            onClose={handleCloseModal}
          />
          <div className="modal-backdrop show"></div>
        </>
      )}
    </Layout>
  );
}
